import React, { useEffect, useState } from 'react';
import { CheckIcon } from '@heroicons/react/20/solid';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { PageHeader, TWButton, TWModal } from '../../components/ui';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import subscriptionApi from '../../services/subscriptionApi';
import companyApi from '../../services/companyApi';
import cardApi from '../../services/cardApi';
import { setGlobalCompany } from '../../store/slices/companySlice';
import SubscriptionSkeleton from './components/subscription-skeleton';
import './subscription.css';

const Subscription = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const globalCompany = useSelector((state) => state.companyReducer.selected);
  const [subscriptionList, setSubscriptionList] = useState([]);
  const [dialogs, setDialogs] = useState({
    hasCreditCard: false,
  });
  const [hasCreditCard, setHasCreditCard] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState({});
  const [loading, setLoading] = useState({
    listLoading: true,
    upsertLoading: false,
    deleteLoading: false,
  });

  const getDefaultSubscriptionList = async () => {
    setSubscriptionList([]);
    try {
      const list = await subscriptionApi.listDefaults();
      if (list.data.data.length > 0) {
        setSubscriptionList(list.data.data);
      }
    } catch (error) {}
  };

  const getCompany = async () => {
    setLoading((prev) => ({ ...prev, listLoading: true }));
    try {
      const list = await companyApi.getById(globalCompany.id);
      if (list.data.data) {
        setSelectedCompany(list.data.data);
      }
    } catch (error) {
    } finally {
      setLoading((prev) => ({ ...prev, listLoading: false }));
    }
  };

  const onClickSelectSubscription = async (sub) => {
    if (!hasCreditCard) {
      setDialogs((prev) => ({ ...prev, hasCreditCard: true }));
    } else {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));
      try {
        const formData = new FormData();

        for (const key of Object.keys(selectedCompany)) {
          if (key === 'subscriptionid') {
            formData.append(key, sub.id);
          } else {
            formData.append(key, selectedCompany[key]);
          }
        }

        formData.append('trialend', null);

        const config = {
          headers: { 'Content-Type': 'multipart/form-data' },
        };

        const res = await companyApi.upsert(formData, config);
        if (res.data.data) {
          getCompany();
          dispatch(setGlobalCompany(res.data.data));
        }
      } catch (error) {
      } finally {
        setLoading((prev) => ({ ...prev, upsertLoading: false }));
      }
    }
  };

  const getCreditCard = async () => {
    const query = `?companyid=${globalCompany.id}`;
    try {
      const list = await cardApi.list(query);
      if (list.data.data.length > 0) {
        setHasCreditCard(true);
      }
    } catch (error) {
    } finally {
      setLoading((prev) => ({ ...prev, listLoading: false }));
    }
  };

  const onCloseHasCreditCardDialogs = () => {
    setDialogs((prev) => ({ ...prev, hasCreditCard: false }));
  };

  const onClickGoToCreditCard = () => {
    setDialogs((prev) => ({ ...prev, hasCreditCard: false }));
    navigate('/dashboard/settings/bank-accounts');
  };

  useEffect(() => {
    getCompany();
    getCreditCard();
    getDefaultSubscriptionList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalCompany.id]);

  return (
    <div className="subscription-shell min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto max-w-5xl space-y-8">
        <PageHeader
          title="Subscription"
          description="Learn about the pricing and features of our subscription plans."
        />

        {loading.listLoading ? (
          <SubscriptionSkeleton
            hasSubscription={
              globalCompany?.subscription !== null &&
              globalCompany?.subscription !== undefined
            }
          />
        ) : (
          <div className="reveal">
            {selectedCompany.subscription !== null ? (
              <div>
                <div className="relative flex flex-col overflow-hidden rounded-md border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800 sm:p-8">
                  <div className="flex-1">
                    {selectedCompany.subscription?.name ===
                      'Ultimate Solution' && (
                      <span className="mb-3 inline-flex items-center rounded-md bg-blue-600 px-3.5 py-1 text-xs font-semibold text-white shadow-sm dark:bg-sky-500">
                        Most popular
                      </span>
                    )}
                    <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                      {selectedCompany.subscription?.name}
                    </h3>
                    <p className="mt-4 flex items-baseline text-slate-900 dark:text-slate-100">
                      <span className="text-5xl font-bold tracking-tight">
                        {selectedCompany.subscription?.orderfeerate}%
                      </span>
                      <span className="ml-1 text-xl font-semibold">
                        /per order
                      </span>
                    </p>
                    <p className="mt-6 text-slate-600 dark:text-slate-300">
                      {selectedCompany.subscription?.name ===
                      'Ultimate Solution'
                        ? 'Unlock a complimentary kiosk with your subscription.'
                        : 'Access to every tool, feature, and capability our platform offers'}
                    </p>

                    {/* Feature list */}
                    <ul className="mt-6 space-y-4">
                      <li className="flex">
                        <CheckIcon
                          className="h-6 w-6 flex-shrink-0 text-blue-600 dark:text-sky-400"
                          aria-hidden="true"
                        />
                        <span className="ml-3 text-slate-600 dark:text-slate-300">
                          Unlimited Orders
                        </span>
                      </li>
                      <li className="flex">
                        <CheckIcon
                          className="h-6 w-6 flex-shrink-0 text-blue-600 dark:text-sky-400"
                          aria-hidden="true"
                        />
                        <span className="ml-3 text-slate-600 dark:text-slate-300">
                          Unlimited Kiosks
                        </span>
                      </li>
                      <li className="flex">
                        <CheckIcon
                          className="h-6 w-6 flex-shrink-0 text-blue-600 dark:text-sky-400"
                          aria-hidden="true"
                        />
                        <span className="ml-3 text-slate-600 dark:text-slate-300">
                          Unlimited Software Upgrades
                        </span>
                      </li>
                      <li className="flex">
                        <CheckIcon
                          className="h-6 w-6 flex-shrink-0 text-blue-600 dark:text-sky-400"
                          aria-hidden="true"
                        />
                        <span className="ml-3 text-slate-600 dark:text-slate-300">
                          White Label Branding
                        </span>
                      </li>
                      <li className="flex">
                        <CheckIcon
                          className="h-6 w-6 flex-shrink-0 text-blue-600 dark:text-sky-400"
                          aria-hidden="true"
                        />
                        <span className="ml-3 text-slate-600 dark:text-slate-300">
                          Multi-Store Management
                        </span>
                      </li>
                      <li className="flex">
                        <CheckIcon
                          className="h-6 w-6 flex-shrink-0 text-blue-600 dark:text-sky-400"
                          aria-hidden="true"
                        />
                        <span className="ml-3 text-slate-600 dark:text-slate-300">
                          Enterprise Cloud Hosted
                        </span>
                      </li>
                      <li className="flex">
                        <CheckIcon
                          className="h-6 w-6 flex-shrink-0 text-blue-600 dark:text-sky-400"
                          aria-hidden="true"
                        />
                        <span className="ml-3 text-slate-600 dark:text-slate-300">
                          Free Implementation
                        </span>
                      </li>
                      <li className="flex">
                        <CheckIcon
                          className="h-6 w-6 flex-shrink-0 text-blue-600 dark:text-sky-400"
                          aria-hidden="true"
                        />
                        <span className="ml-3 text-slate-600 dark:text-slate-300">
                          Remote Support and Assistance
                        </span>
                      </li>
                      <li className="flex">
                        <CheckIcon
                          className="h-6 w-6 flex-shrink-0 text-blue-600 dark:text-sky-400"
                          aria-hidden="true"
                        />
                        <span className="ml-3 text-slate-600 dark:text-slate-300">
                          {selectedCompany.subscription?.monthlyfee !== 0
                            ? `$ ${selectedCompany.subscription?.monthlyfee} / Monthly Fee`
                            : 'No Monthly Fees'}
                        </span>
                      </li>
                      {selectedCompany.subscription?.name ===
                        'Ultimate Solution' && (
                        <li className="flex">
                          <CheckIcon
                            className="h-6 w-6 flex-shrink-0 text-blue-600 dark:text-sky-400"
                            aria-hidden="true"
                          />
                          <span className="ml-3 font-semibold text-blue-700 dark:text-sky-300">
                            Hardware Included
                          </span>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
                <div className="text-gray-400 text-xs mt-2">
                  Credit card processing fees are not included in the
                  subscriptions, All transactions will apply regular credit card
                  processing fees by a third-party company. A $350
                  <b> Inactive Device Fee</b> applies if the device is offline
                  for any 7 days within a 30-day period.
                </div>
              </div>
            ) : (
              <div className="space-y-10">
                {/* Pricing Cards */}
                <div className="grid grid-cols-1 gap-8 xl:grid-cols-3 items-stretch">
                  {subscriptionList.map((subscription, idx) => {
                    const isPopular =
                      subscription?.name === 'Ultimate Solution';

                    const features = [
                      'Unlimited Orders',
                      'Unlimited Kiosks',
                      'Unlimited Software Upgrades',
                      'White Label Branding',
                      'Multi-Store Management',
                      'Enterprise Cloud Hosted',
                      'Free Implementation',
                      'Remote Support and Assistance',
                      subscription?.monthlyfee !== 0
                        ? `$ ${subscription?.monthlyfee} / Monthly Fee`
                        : 'No Monthly Fees',
                    ];

                    if (isPopular) {
                      features.push('Hardware Included');
                    }

                    return (
                      <div key={idx} className="relative h-full pt-4">
                        {isPopular && (
                          <div className="absolute left-1/2 top-0 z-20 -translate-x-1/2 -translate-y-1/2">
                            <span className="inline-flex rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-1.5 text-xs font-semibold text-white shadow-lg shadow-blue-500/30">
                              Most popular
                            </span>
                          </div>
                        )}

                        <div
                          className={`group relative flex h-full min-h-[620px] flex-col overflow-hidden rounded-3xl border bg-white shadow-sm transition-all duration-300 dark:bg-slate-800 ${
                            isPopular
                              ? 'border-blue-500 ring-1 ring-blue-500/20 shadow-lg shadow-blue-500/10'
                              : 'border-slate-200 hover:border-slate-300 dark:border-slate-700 dark:hover:border-slate-600'
                          }`}
                        >
                          {isPopular && (
                            <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-600 to-indigo-600" />
                          )}

                          <div className="flex flex-1 flex-col p-6 sm:p-8">
                            {/* Header */}
                            <div>
                              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                                {subscription?.name}
                              </h3>

                              <p className="mt-5 flex items-end gap-1 text-slate-900 dark:text-slate-100">
                                <span className="text-6xl font-bold tracking-tight leading-none">
                                  {subscription?.orderfeerate}%
                                </span>
                                <span className="pb-1 text-lg font-medium text-slate-500 dark:text-slate-400">
                                  / per order
                                </span>
                              </p>

                              <p className="mt-5 min-h-[48px] text-sm leading-6 text-slate-600 dark:text-slate-300">
                                {isPopular
                                  ? 'Unlock a complimentary kiosk with your subscription.'
                                  : 'Access to every tool, feature, and capability our platform offers.'}
                              </p>
                            </div>

                            {/* Feature list */}
                            <div className="mt-8 flex-1">
                              <div className="rounded-md bg-slate-50 p-4 dark:bg-slate-900/40">
                                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                                  What’s included
                                </p>

                                <ul className="mt-4 space-y-3">
                                  {features.map((item, i) => (
                                    <li key={i} className="flex items-start">
                                      <CheckIcon
                                        className={`mt-0.5 h-5 w-5 flex-shrink-0 ${
                                          isPopular
                                            ? 'text-blue-600 dark:text-sky-400'
                                            : 'text-slate-700 dark:text-slate-300'
                                        }`}
                                        aria-hidden="true"
                                      />
                                      <span
                                        className={`ml-3 text-sm leading-6 ${
                                          item === 'Hardware Included'
                                            ? 'font-semibold text-blue-700 dark:text-sky-300'
                                            : 'text-slate-600 dark:text-slate-300'
                                        }`}
                                      >
                                        {item}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>

                            {/* Sticky CTA Area */}
                            <div className="mt-8 border-t border-slate-100 pt-6 dark:border-slate-700">
                              <button
                                type="button"
                                onClick={() =>
                                  onClickSelectSubscription(subscription)
                                }
                                disabled={loading.upsertLoading}
                                className={`inline-flex h-11 w-full items-center justify-center rounded-full px-6 text-sm font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-70 ${
                                  isPopular
                                    ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20 hover:bg-blue-700 dark:bg-sky-500 dark:hover:bg-sky-400'
                                    : 'bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white'
                                }`}
                              >
                                {loading.upsertLoading
                                  ? 'Processing...'
                                  : 'Select Plan'}
                              </button>

                              <p className="mt-3 text-center text-xs text-slate-500 dark:text-slate-400">
                                No setup surprises. Transparent subscription
                                pricing.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Compare Table */}
                <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
                  <div className="border-b border-slate-200 px-6 py-5 dark:border-slate-700 sm:px-8">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                      Compare plans
                    </h3>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                      A quick side-by-side breakdown of the key differences.
                    </p>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full border-collapse">
                      <thead>
                        <tr className="bg-slate-50 dark:bg-slate-900/40">
                          <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900 dark:text-slate-100 sm:px-8">
                            Features
                          </th>
                          {subscriptionList.map((subscription, idx) => {
                            const isPopular =
                              subscription?.name === 'Ultimate Solution';

                            return (
                              <th
                                key={idx}
                                className={`px-6 py-4 text-left text-sm font-semibold sm:px-8 ${
                                  isPopular
                                    ? 'text-blue-700 dark:text-sky-300'
                                    : 'text-slate-900 dark:text-slate-100'
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <span>{subscription?.name}</span>
                                  {isPopular && (
                                    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-semibold text-blue-700 dark:bg-sky-500/20 dark:text-sky-300">
                                      Popular
                                    </span>
                                  )}
                                </div>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                        <tr>
                          <td className="px-6 py-4 text-sm font-medium text-slate-900 dark:text-slate-100 sm:px-8">
                            Order fee
                          </td>
                          {subscriptionList.map((subscription, idx) => (
                            <td
                              key={idx}
                              className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300 sm:px-8"
                            >
                              {subscription?.orderfeerate}%
                            </td>
                          ))}
                        </tr>

                        <tr>
                          <td className="px-6 py-4 text-sm font-medium text-slate-900 dark:text-slate-100 sm:px-8">
                            Monthly fee
                          </td>
                          {subscriptionList.map((subscription, idx) => (
                            <td
                              key={idx}
                              className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300 sm:px-8"
                            >
                              {subscription?.monthlyfee !== 0
                                ? `$ ${subscription?.monthlyfee}`
                                : 'No monthly fee'}
                            </td>
                          ))}
                        </tr>

                        <tr>
                          <td className="px-6 py-4 text-sm font-medium text-slate-900 dark:text-slate-100 sm:px-8">
                            Unlimited orders
                          </td>
                          {subscriptionList.map((_, idx) => (
                            <td
                              key={idx}
                              className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300 sm:px-8"
                            >
                              Yes
                            </td>
                          ))}
                        </tr>

                        <tr>
                          <td className="px-6 py-4 text-sm font-medium text-slate-900 dark:text-slate-100 sm:px-8">
                            Unlimited kiosks
                          </td>
                          {subscriptionList.map((_, idx) => (
                            <td
                              key={idx}
                              className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300 sm:px-8"
                            >
                              Yes
                            </td>
                          ))}
                        </tr>

                        <tr>
                          <td className="px-6 py-4 text-sm font-medium text-slate-900 dark:text-slate-100 sm:px-8">
                            White label branding
                          </td>
                          {subscriptionList.map((_, idx) => (
                            <td
                              key={idx}
                              className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300 sm:px-8"
                            >
                              Yes
                            </td>
                          ))}
                        </tr>

                        <tr>
                          <td className="px-6 py-4 text-sm font-medium text-slate-900 dark:text-slate-100 sm:px-8">
                            Multi-store management
                          </td>
                          {subscriptionList.map((_, idx) => (
                            <td
                              key={idx}
                              className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300 sm:px-8"
                            >
                              Yes
                            </td>
                          ))}
                        </tr>

                        <tr>
                          <td className="px-6 py-4 text-sm font-medium text-slate-900 dark:text-slate-100 sm:px-8">
                            Free implementation
                          </td>
                          {subscriptionList.map((_, idx) => (
                            <td
                              key={idx}
                              className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300 sm:px-8"
                            >
                              Yes
                            </td>
                          ))}
                        </tr>

                        <tr>
                          <td className="px-6 py-4 text-sm font-medium text-slate-900 dark:text-slate-100 sm:px-8">
                            Hardware included
                          </td>
                          {subscriptionList.map((subscription, idx) => (
                            <td key={idx} className="px-6 py-4 text-sm sm:px-8">
                              {subscription?.name === 'Ultimate Solution' ? (
                                <span className="font-semibold text-blue-700 dark:text-sky-300">
                                  Yes
                                </span>
                              ) : (
                                <span className="text-slate-400 dark:text-slate-500">
                                  —
                                </span>
                              )}
                            </td>
                          ))}
                        </tr>

                        <tr>
                          <td className="px-6 py-4 text-sm font-medium text-slate-900 dark:text-slate-100 sm:px-8">
                            Support
                          </td>
                          {subscriptionList.map((_, idx) => (
                            <td
                              key={idx}
                              className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300 sm:px-8"
                            >
                              Remote support
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Small disclaimer */}
                <div className="rounded-md border border-slate-200 bg-white/60 p-4 text-xs text-slate-500 shadow-sm dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-400">
                  <ul className="space-y-1">
                    <li>
                      * Ultimate Solution includes a free kiosk only for select
                      restaurants.
                    </li>
                    <li>
                      Credit card processing fees are not included in the
                      subscriptions, All transactions will apply regular credit
                      card processing fees by a third-party company.
                    </li>
                    <li>
                      A $350 <strong>Inactive Device Fee</strong> applies if the
                      device is offline for any 7 days within a 30-day period.
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <TWModal
        open={dialogs.hasCreditCard}
        onClose={onCloseHasCreditCardDialogs}
        title="Add a credit card"
        size="lg"
        showCloseButton
        dismissible={false}
        footer={
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
            <TWButton
              variant="default"
              label="Later"
              onClick={() => onCloseHasCreditCardDialogs()}
              className="w-full sm:w-auto"
            />
            <TWButton
              variant="primary"
              label="Add credit card"
              disabled={loading.upsertLoading}
              loading={loading.upsertLoading}
              onClick={() => onClickGoToCreditCard()}
              className="w-full sm:w-auto"
            />
          </div>
        }
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-200">
            <ExclamationTriangleIcon className="h-6 w-6 flex-shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-semibold">Payment method required</p>
              <p className="text-sm">
                We couldn&apos;t find a saved credit card for your account. Add
                one to continue.
              </p>
            </div>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            You&apos;ll be redirected to <strong>Bank accounts</strong> to add a
            card.
          </p>
        </div>
      </TWModal>
    </div>
  );
};

export default Subscription;
